import React, { useState, useEffect } from 'react';
import { FiInfo, FiChevronDown, FiChevronUp, FiCloud, FiTarget, FiCpu, FiSearch, FiActivity, FiCheck, FiClock } from 'react-icons/fi';
import { IconType } from 'react-icons';

export interface ScoringMethod {
  value: string;
  label: string;
  description: string;
  Icon: IconType;
  advanced?: boolean;
  requiresAws?: boolean;
  isAsync?: boolean;
}

export const SCORING_METHODS: ScoringMethod[] = [
  {
    value: 'exact_match',
    label: 'Exact Match',
    description: 'Exact string comparison (case-insensitive)',
    Icon: FiCheck,
  },
  {
    value: 'contains',
    label: 'Contains',
    description: 'Check if expected output is contained in actual output',
    Icon: FiSearch,
  },
  {
    value: 'similarity',
    label: 'Similarity Score',
    description: 'Levenshtein distance-based similarity (0-1)',
    Icon: FiActivity,
  },
  {
    value: 'semantic_similarity',
    label: 'Semantic Similarity',
    description: 'Embedding-based semantic comparison using AI models',
    Icon: FiCpu,
    advanced: true,
  },
  {
    value: 'llm_judge',
    label: 'LLM Judge',
    description: 'AI-powered evaluation with custom rubrics',
    Icon: FiTarget,
    advanced: true,
  },
  {
    value: 'bedrock_evaluation',
    label: 'AWS Bedrock Evaluation',
    description: 'Enterprise-grade evaluation using AWS Bedrock (async, 5-30 min)',
    Icon: FiCloud,
    advanced: true,
    requiresAws: true,
    isAsync: true,
  },
];

export interface EmbeddingProvider {
  value: string;
  label: string;
  models: string[];
}

export const EMBEDDING_PROVIDERS: EmbeddingProvider[] = [
  {
    value: 'openai',
    label: 'OpenAI',
    models: ['text-embedding-3-small', 'text-embedding-3-large', 'text-embedding-ada-002'],
  },
  {
    value: 'sentence_transformers',
    label: 'Sentence Transformers (Local)',
    models: ['all-MiniLM-L6-v2', 'all-mpnet-base-v2'],
  },
];

export interface BedrockTaskType {
  value: string;
  label: string;
  description: string;
}

export const BEDROCK_TASK_TYPES: BedrockTaskType[] = [
  { value: 'QuestionAndAnswer', label: 'Question & Answer', description: 'For Q&A style prompts' },
  { value: 'Generation', label: 'Generation', description: 'For text generation tasks' },
  { value: 'Summarization', label: 'Summarization', description: 'For summarization tasks' },
  { value: 'Classification', label: 'Classification', description: 'For classification tasks' },
];

export interface BedrockEvaluationMetric {
  value: string;
  label: string;
  description: string;
}

export const BEDROCK_EVALUATION_METRICS: BedrockEvaluationMetric[] = [
  { value: 'Builtin.Accuracy', label: 'Accuracy', description: 'Semantic accuracy using BERTScore' },
  { value: 'Builtin.Robustness', label: 'Robustness', description: 'Consistency with perturbations' },
  { value: 'Builtin.Toxicity', label: 'Toxicity', description: 'Harmful content detection' },
];

interface JudgeRubric {
  id: string;
  name: string;
}

interface ScoringMethodSelectorProps {
  value: string;
  onChange: (value: string) => void;
  judgeRubricId?: string | null;
  onJudgeRubricChange?: (id: string | null) => void;
  judgeRubrics?: JudgeRubric[];
  onCreateRubric?: () => void;
  embeddingProvider?: string;
  onEmbeddingProviderChange?: (provider: string) => void;
  embeddingModel?: string;
  onEmbeddingModelChange?: (model: string) => void;
  similarityThreshold?: number;
  onSimilarityThresholdChange?: (threshold: number) => void;
  bedrockTaskType?: string;
  onBedrockTaskTypeChange?: (taskType: string) => void;
  bedrockMetrics?: string[];
  onBedrockMetricsChange?: (metrics: string[]) => void;
  hasAwsCredentials?: boolean;
  passThreshold?: number;
  onPassThresholdChange?: (threshold: number) => void;
  disabled?: boolean;
  showAdvanced?: boolean;
  compact?: boolean;
}

const ScoringMethodSelector: React.FC<ScoringMethodSelectorProps> = ({
  value,
  onChange,
  judgeRubricId,
  onJudgeRubricChange,
  judgeRubrics = [],
  onCreateRubric,
  embeddingProvider,
  onEmbeddingProviderChange,
  embeddingModel,
  onEmbeddingModelChange,
  similarityThreshold = 0.8,
  onSimilarityThresholdChange,
  bedrockTaskType = 'QuestionAndAnswer',
  onBedrockTaskTypeChange,
  bedrockMetrics = ['Builtin.Accuracy'],
  onBedrockMetricsChange,
  hasAwsCredentials = false,
  passThreshold = 0.7,
  onPassThresholdChange,
  disabled = false,
  showAdvanced = true,
  compact = false,
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const selectedMethod = SCORING_METHODS.find((m) => m.value === value) ?? SCORING_METHODS[1];

  const availableMethods = SCORING_METHODS.filter(
    (method) => !method.requiresAws || hasAwsCredentials
  );

  useEffect(() => {
    if (
      value === 'llm_judge' ||
      value === 'semantic_similarity' ||
      value === 'bedrock_evaluation'
    ) {
      setShowOptions(true);
    }
  }, [value]);

  const handleMethodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    if (newValue !== 'llm_judge' && onJudgeRubricChange) {
      onJudgeRubricChange(null);
    }
    if (newValue !== 'semantic_similarity') {
      if (onEmbeddingProviderChange) onEmbeddingProviderChange('openai');
      if (onEmbeddingModelChange) onEmbeddingModelChange('text-embedding-3-small');
    }
  };

  const currentProvider =
    EMBEDDING_PROVIDERS.find((p) => p.value === embeddingProvider) ?? EMBEDDING_PROVIDERS[0];

  return (
    <div className="space-y-4">
      {/* Main Scoring Method Selector */}
      <div>
        <label htmlFor="eval-scoring-method" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Scoring Method
        </label>
        <div className="relative">
          <select
                                                                                  id="eval-scoring-method"
            value={value}
            onChange={handleMethodChange}
            disabled={disabled}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white transition-colors border-gray-300 dark:border-gray-600 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {availableMethods.map((method) => (
              <option key={method.value} value={method.value}>
                <method.Icon className="inline w-4 h-4 mr-1" />{method.label}
              </option>
            ))}
          </select>
        </div>
        {!compact && (
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
            <FiInfo className="w-4 h-4" />
            {selectedMethod.description}
          </p>
        )}
      </div>

      {/* Pass Threshold */}
      <div>
        <label htmlFor="eval-pass-threshold" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Pass Threshold{' '}
          <span className="ml-2 text-gray-500 dark:text-gray-400 font-normal">
            ({(passThreshold * 100).toFixed(0)}%)
          </span>
        </label>
        <input
                                                                                  id="eval-pass-threshold"
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={passThreshold}
          onChange={(e) => onPassThresholdChange?.(Number.parseFloat(e.target.value))}
          disabled={disabled}
          className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer"
        />
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
          <span>0%</span>
          <span>50%</span>
          <span>100%</span>
        </div>
      </div>

      {/* Advanced Options Toggle */}
      {showAdvanced &&
        (value === 'llm_judge' ||
          value === 'semantic_similarity' ||
          value === 'bedrock_evaluation') && (
          <button
            type="button"
            onClick={() => setShowOptions(!showOptions)}
            className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            {showOptions ? <FiChevronUp /> : <FiChevronDown />}
            {showOptions ? 'Hide' : 'Show'} Advanced Options
          </button>
        )}

      {/* LLM Judge Options */}
      {showOptions && value === 'llm_judge' && (
        <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <FiTarget className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            <h4 className="font-medium text-gray-900 dark:text-white">LLM Judge Configuration</h4>
          </div>

          <div>
            <label htmlFor="eval-judge-rubric" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Judge Rubric
            </label>
            <div className="flex gap-2">
              <select
                                                                                  id="eval-judge-rubric"
                value={judgeRubricId ?? ''}
                onChange={(e) => onJudgeRubricChange?.(e.target.value || null)}
                disabled={disabled}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Default (General Quality)</option>
                {judgeRubrics.map((rubric) => (
                  <option key={rubric.id} value={rubric.id}>
                    {rubric.name}
                  </option>
                ))}
              </select>
              {onCreateRubric && (
                <button
                  type="button"
                  onClick={onCreateRubric}
                  className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                >
                  + New
                </button>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Rubrics define evaluation criteria and scoring weights
            </p>
          </div>
        </div>
      )}

      {/* Semantic Similarity Options */}
      {showOptions && value === 'semantic_similarity' && (
        <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <FiCpu className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            <h4 className="font-medium text-gray-900 dark:text-white">
              Semantic Similarity Configuration
            </h4>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="eval-embedding-provider" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Embedding Provider
              </label>
              <select
                                                                                  id="eval-embedding-provider"
                value={embeddingProvider ?? 'openai'}
                onChange={(e) => {
                  onEmbeddingProviderChange?.(e.target.value);
                  const provider = EMBEDDING_PROVIDERS.find((p) => p.value === e.target.value);
                  if (provider && onEmbeddingModelChange) {
                    onEmbeddingModelChange(provider.models[0]);
                  }
                }}
                disabled={disabled}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {EMBEDDING_PROVIDERS.map((provider) => (
                  <option key={provider.value} value={provider.value}>
                    {provider.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="eval-embedding-model" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Embedding Model
              </label>
              <select
                                                                                  id="eval-embedding-model"
                value={embeddingModel ?? currentProvider.models[0]}
                onChange={(e) => onEmbeddingModelChange?.(e.target.value)}
                disabled={disabled}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {currentProvider.models.map((model) => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="eval-similarity-threshold" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Similarity Threshold{' '}
              <span className="ml-2 text-gray-500 dark:text-gray-400 font-normal">
                ({(similarityThreshold * 100).toFixed(0)}%)
              </span>
            </label>
            <input
                                                                                  id="eval-similarity-threshold"
              type="range"
              min="0.5"
              max="1"
              step="0.05"
              value={similarityThreshold}
              onChange={(e) => onSimilarityThresholdChange?.(Number.parseFloat(e.target.value))}
              disabled={disabled}
              className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span>50% (Loose)</span>
              <span>75%</span>
              <span>100% (Strict)</span>
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Minimum cosine similarity score to consider a match
            </p>
          </div>
        </div>
      )}

      {/* AWS Bedrock Evaluation Options */}
      {showOptions && value === 'bedrock_evaluation' && (
        <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <FiCloud className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            <h4 className="font-medium text-gray-900 dark:text-white">
              AWS Bedrock Evaluation Configuration
            </h4>
          </div>

          <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-md border border-amber-200 dark:border-amber-700">
            <div className="flex items-start gap-2">
              <FiClock className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  Asynchronous Evaluation
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                  Bedrock evaluations run asynchronously and typically take 5-30 minutes to complete.
                  You can navigate away - results will be available when the evaluation finishes.
                </p>
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="eval-task-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Task Type
            </label>
            <select
                                                                                  id="eval-task-type"
              value={bedrockTaskType}
              onChange={(e) => onBedrockTaskTypeChange?.(e.target.value)}
              disabled={disabled}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {BEDROCK_TASK_TYPES.map((taskType) => (
                <option key={taskType.value} value={taskType.value}>
                  {taskType.label} - {taskType.description}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Select the task type that best matches your evaluation scenario
            </p>
          </div>

          <div>
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Evaluation Metrics
            </span>
            <div className="space-y-2">
              {BEDROCK_EVALUATION_METRICS.map((metric) => (
                <label key={metric.value} className="flex items-start gap-2" aria-label={metric.label}>
                  <input
                    type="checkbox"
                    checked={bedrockMetrics.includes(metric.value)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onBedrockMetricsChange?.([...bedrockMetrics, metric.value]);
                      } else if (bedrockMetrics.length > 1) {
                        onBedrockMetricsChange?.(bedrockMetrics.filter((m) => m !== metric.value));
                      }
                    }}
                    disabled={disabled}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 mt-0.5"
                  />
                  <div>
                    <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                      {metric.label}
                    </span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{metric.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-md text-sm">
            <p className="text-blue-900 dark:text-blue-100">
              <strong>Bedrock Evaluation</strong> uses AWS managed infrastructure for enterprise-grade
              evaluations. Accuracy and Robustness metrics internally use BERTScore.
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
              Best for: Production workloads, compliance requirements, large-scale evaluations
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScoringMethodSelector;
